import * as React from 'react';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

// @ts-expect-error
import bacon from 'baconjs';

import {formEncodedApi} from 'nutshell-core/api';
import {Routing} from 'nutshell-core/routing';
import type {User} from 'nutshell-core/types';
import {getEntityTypeForPathname} from 'nutshell-core/url/get-entity-type-from-pathname';

import {useAgentPresence} from 'shells/inbox/hooks/use-agent-presence';

import {App as AppModule} from '../app/index';
// @ts-expect-error
import {Dispatcher} from '../infrastructure/dispatcher';
import {bindKeyboardShortcuts, unbindKeyboardShortcuts} from '../keyboard-shortcuts';
import {store} from '../store';

import {AppRoutes} from './app-routes';

// Location has changed only if one of these properties has changed- there is a
// 4th 'key' property on our location object that changes between checks even
// if we're actually in the same state, and we don't want to treat location as
// changed in that case.
// @ts-expect-error
function hasLocationChanged(old, current) {
    return (
        old.pathname !== current.pathname ||
        old.search !== current.search ||
        old.hash !== current.hash
    );
}

type Props = {
    appState: typeof bacon.Property;
    dispatcher: Dispatcher<any>;
} & ContextRouter;

type AppState = {
    initializedEntities: string[];
    isInitialized: boolean;
    user: User | null | undefined;
    accounts: any;
    contacts: any;
    leads: any;
    notifications: any[];
};

// Our app wrapped in bacon.js appState.
// Deprecated technology, but not worth removing yet.
// https://baconjs.github.io/
export function AppWrappedInBacon(props: Props) {
    const {appState: baconAppState, dispatcher, location, ...restProps} = props;
    const [appState, setAppState] = React.useState<AppState>({
        isInitialized: false,
        user: null,
        accounts: {},
        contacts: {},
        leads: {},
        notifications: [],
        // @ts-expect-error
        initializedEntities: [getEntityTypeForPathname(location.pathname)],
    });

    useAgentPresence();

    const prevLocation = React.useRef(location);

    const handleNutshellVersionReceived = (e: Event) => {
        // @ts-expect-error
        store.dispatch(AppModule.setAppVersion(e.detail));
    };

    const handleUrlChanges = React.useCallback(
        (newLocation) => {
            // Get the entire url
            const urlState = newLocation;

            // Pull apart the queryString (everything after the question mark)
            // and turn each part into separate parts of entire 'app' state
            const deparamedQuery = Routing.deparam(urlState.search.replace(/^\?/, ''));
            dispatcher.push('url', {
                ...urlState,
                query: deparamedQuery,
            });

            // If the entity type has changed (going from a client-routed page to a list page),
            // we may need to refetch the detail api response if it was previously loaded
            if (prevLocation.current.pathname !== newLocation.pathname) {
                const entityType = getEntityTypeForPathname(newLocation.pathname);
                // @ts-expect-error
                if (appState.initializedEntities.includes(entityType)) {
                    // @ts-expect-error
                    if (appState[entityType]) {
                        // @ts-expect-error
                        appState[entityType].list.refetchPage();
                    }
                } else {
                    // If this entity type hasn't been loaded before, we don't need to refetch,
                    // but record it as initialized so we can refetch next time.
                    setAppState((prevState) => ({
                        ...prevState,
                        // @ts-expect-error
                        initializedEntities: prevState.initializedEntities.concat(entityType),
                    }));
                }

                $('body').trigger(Nut.events.global.pageWasChanged);
            }

            // Update the modal url to display on dismiss
            Modal.updateCurrentPageUrl();
        },
        [dispatcher, prevLocation, appState]
    );

    React.useMemo(() => {
        if (hasLocationChanged(prevLocation.current, location)) {
            handleUrlChanges(location);
            prevLocation.current = location;
        }
    }, [handleUrlChanges, location]);

    React.useEffect(() => {
        if (NutClientConfig.shouldSetTimezone) {
            formEncodedApi.post('/dashboard/timezone.json', {
                timezone: new Intl.DateTimeFormat().resolvedOptions().timeZone,
            });
        }

        store.dispatch(AppModule.setAppVersion(NutshellFrontendSettings.appVersion));

        document.addEventListener('Nutshell version checked', handleNutshellVersionReceived);

        return () => {
            document.removeEventListener('Nutshell version checked', handleNutshellVersionReceived);
        };
    }, []);

    React.useEffect(() => {
        if (appState.isInitialized) {
            // signal to legacy JS / backbone models that we're mounted (and any
            // XHR requests are now in flight)
            $('body').trigger(Nut.events.global.reactWasMountedAndInitialized);
        }
    }, [appState.isInitialized]);

    React.useEffect(() => {
        // @ts-expect-error
        const unsubscribe = baconAppState.onValue((newBaconAppState) => {
            // Not sure why, but this sometimes throws an error.
            try {
                setAppState((prevState) => ({
                    ...prevState,
                    ...newBaconAppState,
                    isInitialized: true,
                }));
            } catch (e: any) {
                // @ts-expect-error
                if (typeof window.trackJs !== 'undefined') {
                    // @ts-expect-error
                    window.trackJs.track(e);
                }
            }
        });

        // Necessary to "kick off" our bacon part of the app state from the URL
        handleUrlChanges(location);

        return () => {
            unsubscribe();
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        if (appState.user) {
            if (appState.user.hasDisabledShortcuts) {
                unbindKeyboardShortcuts();
            } else {
                bindKeyboardShortcuts(appState.user.permissions.canAccessSetup);
            }
        }

        return () => {
            // Clean up shortcuts when component unmounts
            unbindKeyboardShortcuts();
        };
    }, [appState.user]);

    return (
        <AppRoutes
            isInitialized={appState.isInitialized}
            user={appState.user}
            contacts={appState.contacts}
            leads={appState.leads}
            accounts={appState.accounts}
            notifications={appState.notifications}
            location={location}
            {...restProps}
        />
    );
}
